import FeedbackRegenerationDialog from 'components/Grading/FeedbackRegenerationDialog';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import useGradingDraftAutosave from 'hooks/grading/useGradingDraftAutosave';
import useElapsedTime from 'hooks/useElapsedTime';
import { isEqual, omit } from 'lodash';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import batchService from 'services/batch';
import submissionService from 'services/submission';
import { pruneEmptyContext } from 'utils/chat/instructorHugoContext';
import {
    getFeedbackRegenerationState,
    getPendingFeedbackItemIds,
    mergeFeedbackHistoryUpdates,
} from 'utils/feedbackRegeneration';
import {
    filterNavigationOptions,
    getGradingNavigationTitle,
    getGradingSectionOptions,
    getSubmissionSections,
    isGroupGradingAssignment,
    sortNavigationOptions,
} from 'utils/gradingNavigation';
import {
    getAIPreview,
    getCriterionMaxPoints,
    getRubricMaxPoints,
    getSuggestedGradeCriteria,
    getTemplatedFeedback,
    isValidRate,
    scrollElementIntoView,
    stripHTML,
} from 'utils/helper';
import {
    getEffectiveCompletionStatus,
    getRubricOptionLevel,
    isUnscoredRubric,
    normalizeCompletionStatus,
    shouldShowCompletionReviewWarning,
    usesCompleteIncompleteGrading,
} from 'utils/rubricScoring';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const getLatestFeedbackHistoryId = (submission) => {
    const history = submission?.feedback_history ?? [];
    return history[history.length - 1]?.id ?? null;
};

// returns numeric rubric points for a selected option.
const getOptionPoints = (criterion, optionId) => {
    const option = criterion?.options?.find(({ id }) => id === optionId);
    const points = Number(option?.rate);
    return Number.isFinite(points) ? points : undefined;
};

// normalizes a grade override into a finite number when possible.
const getOverridePoints = (value) => {
    if (value === undefined || value === null || value === '') return undefined;
    const points = Number(value);
    return Number.isFinite(points) ? points : undefined;
};

// resolves partial grading status while supporting legacy submissions.
const getSubmissionGradingStatus = (submission) => {
    if (submission?.grading_status) return submission.grading_status;
    return submission?.grading ? 'confirmed' : 'not_started';
};

// returns the persisted draft or confirmed grading used by the form.
const getSubmissionWorkingGrading = (submission) => {
    const workingGrading = submission?.grading_draft ?? submission?.grading;
    const hasWorkingField = (field) => Object.prototype.hasOwnProperty.call(workingGrading ?? {}, field);

    return {
        criteria: workingGrading?.criteria ?? {},
        feedback: hasWorkingField('feedback') ? workingGrading.feedback : (submission?.ai?.feedback ?? null),
        inline_comments: hasWorkingField('inline_comments')
            ? workingGrading.inline_comments
            : (submission?.ai?.inline_comments ?? []),
        grading_explanations: hasWorkingField('grading_explanations')
            ? workingGrading.grading_explanations
            : (submission?.grading_explanations ?? {}),
        status: workingGrading?.status,
    };
};

// returns draft overrides before falling back to confirmed overrides.
const getSubmissionWorkingGradeOverrides = (submission) => {
    return submission?.grade_overrides_draft ?? submission?.grade_overrides ?? {};
};

// calculates the displayed total from reviewed and suggested criteria.
const getGradingTotal = (criteria, grading, gradeOverrides, suggestedGrading) => {
    const totalOverride = getOverridePoints(gradeOverrides?.total);
    if (totalOverride !== undefined) return cleanNumber(totalOverride);

    return criteria.reduce((total, criterion) => {
        const criterionOverride = getOverridePoints(gradeOverrides?.[criterion.id]);
        const criterionPoints =
            criterionOverride ??
            getOptionPoints(criterion, grading?.criteria?.[criterion.id]) ??
            getOptionPoints(criterion, suggestedGrading?.[criterion.id]?.optionId);
        return cleanNumber(total + (criterionPoints ?? 0));
    }, 0);
};

// calculates a complete ai-suggested grade when all criteria are present.
const getSuggestedGrade = (criteria, submission) => {
    const criterionPoints = criteria.map((criterion) => {
        return getOptionPoints(criterion, submission?.ai?.grading?.[criterion.id]?.optionId);
    });

    return criterionPoints.some((points) => points === undefined)
        ? null
        : cleanNumber(criterionPoints.reduce((total, points) => total + points, 0));
};

// builds the partial grading payload persisted by autosave.
const getDraftSavePayload = ({ form, gradeOverrides, hasSeenFeedback }) => ({
    grading_draft: form,
    grade_overrides_draft: gradeOverrides,
    grading_status: 'in_progress',
    has_seen_feedback: hasSeenFeedback,
});

// builds the unsaved grading context exposed to instructor hugo.
const buildUnsavedGradeContext = ({ currentAssignment, currentSubmission, form, gradeOverrides, hasChanges }) => {
    const criteria = currentAssignment?.rubric?.criteria;
    if (!hasChanges || !currentSubmission || !criteria?.length) return null;
    const isUnscored = isUnscoredRubric(currentAssignment);

    const gradeCriteria = criteria
        .map((criterion) => {
            const optionId = form.criteria?.[criterion.id];
            if (isUnscored) {
                if (!optionId) return null;
                return pruneEmptyContext({
                    criterion_id: criterion.id,
                    option_id: optionId,
                    title: criterion.title,
                    level: getRubricOptionLevel(criterion, optionId),
                    explanation: form.grading_explanations?.[criterion.id],
                });
            }
            const rubricPoints = getOptionPoints(criterion, optionId);
            const override = getOverridePoints(gradeOverrides?.[criterion.id]);
            const isOverridden = override !== undefined;
            const points = isOverridden ? override : rubricPoints;
            if (!optionId && points === undefined) return null;

            return pruneEmptyContext({
                criterion_id: criterion.id,
                option_id: optionId,
                title: criterion.title,
                points,
                rubric_points: isOverridden ? rubricPoints : undefined,
                overridden: isOverridden ? true : undefined,
                max_points: getCriterionMaxPoints(criterion),
                explanation: form.grading_explanations?.[criterion.id],
            });
        })
        .filter(Boolean);
    if (isUnscored) {
        if (!gradeCriteria.length) return null;
        return pruneEmptyContext({
            grades: {
                current: {
                    is_unsaved: true,
                    status: usesCompleteIncompleteGrading(currentAssignment)
                        ? getEffectiveCompletionStatus({ criteria, selections: form.criteria, status: form.status })
                        : 'reviewed',
                    criteria: gradeCriteria,
                },
            },
        });
    }
    const totalOverride = getOverridePoints(gradeOverrides?.total);
    const criteriaTotalPoints = gradeCriteria.reduce((total, criterion) => total + (criterion.points ?? 0), 0);
    const totalPoints = totalOverride ?? criteriaTotalPoints;
    const totalPossible = getRubricMaxPoints(criteria);
    if (!gradeCriteria.length && totalOverride === undefined) return null;

    return pruneEmptyContext({
        grades: {
            current: {
                is_unsaved: true,
                total_points: totalPoints,
                criteria_total_points: totalOverride !== undefined ? criteriaTotalPoints : undefined,
                total_overridden: totalOverride !== undefined ? true : undefined,
                total_possible: totalPossible,
                percentage: totalPossible ? Math.round((totalPoints / totalPossible) * 1000) / 10 : undefined,
                criteria: gradeCriteria,
            },
        },
    });
};

export const cleanNumber = (number) => {
    return Number(number?.toFixed(2));
};

const PageProvider = (props) => {
    const {
        course,
        accountUsers,
        currentAssignment,
        submissions: allSubmissions,
        setBatch,
        setInstructorHugoLiveContext,
        setSubmissions,
    } = useApp();
    const { auth, getAccount } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { joyrideRun, setJoyridePaused, setNextCallback, setPrevCallback, performNext } = useJoyride();
    const { onBeforeNavigate } = useNavigationGuard();
    const { id: currentAssignmentId, submissionId: currentSubmissionId } = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { onMessage } = useWebsocket();

    // tracks which submission initialized the local partial grading form.
    const initializedSubmissionIdRef = useRef(null);
    // tracks the latest history event already merged into the local form.
    const syncedFeedbackHistoryRef = useRef({ historyId: null, submissionId: null });

    const [selectedFileId, setSelectedFileId] = useState('');
    const [sort, setSort] = useState('default');
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [form, setForm] = useState({
        criteria: {},
        feedback: null,
        inline_comments: null,
        grading_explanations: {},
    });
    const [gradeOverrides, setGradeOverrides] = useState({});
    const [meta, setMeta] = useState({
        hasSeenFeedback: true,
        sessionStart: new Date().getTime(),
        excludedTime: 0,
    });
    const [tab, setTab] = useState('grades');
    const [selectedCriterionId, setSelectedCriterionId] = useState(null);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [selectedInlineCommentId, setSelectedInlineCommentId] = useState(null);
    const [isGradesGenerating, setIsGradesGenerating] = useState(false);
    const [showFailedDialog, setShowFailedDialog] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);
    // controls the feedback selection and custom prompt dialog
    const [isFeedbackRegenerationDialogOpen, setIsFeedbackRegenerationDialogOpen] = useState(false);

    const { createSession, addSessionToSubmission } = useElapsedTime({ meta, setMeta });
    const isUnscored = isUnscoredRubric(currentAssignment);
    const usesCompletionStatus = isUnscored && usesCompleteIncompleteGrading(currentAssignment);

    const submissions = useMemo(() => {
        return allSubmissions?.filter(
            ({ ai, grading, is_failed, locked }) => ((Boolean(ai) || is_failed) && !Boolean(grading)) || locked
        );
    }, [allSubmissions]);

    const isGroupAssignment = isGroupGradingAssignment(currentAssignment);
    const sectionOptions = useMemo(() => {
        const account = accountUsers?.find(({ owner_account_id }) => owner_account_id === auth.id);
        const allowedSectionIds =
            auth.role === 'ta' && currentAssignment?.api_assignment_id
                ? (account?.sections ?? []).map(({ id }) => id)
                : undefined;
        return getGradingSectionOptions(submissions ?? [], allowedSectionIds);
    }, [accountUsers, auth.id, auth.role, currentAssignment?.api_assignment_id, submissions]);
    const sectionId = sectionOptions.some(({ id }) => id === selectedSectionId) ? selectedSectionId : '';

    useEffect(() => {
        setSelectedSectionId('');
    }, [currentAssignmentId]);

    // builds navigation rows with partial grading status and working totals.
    const allHeaderNavigationOptions = useMemo(() => {
        const rubricCriteria = currentAssignment?.rubric?.criteria ?? [];
        const options = submissions?.map((submission) => {
            const { id } = submission;
            const sections = getSubmissionSections(submission);
            const isCurrentSubmission = id === currentSubmissionId;
            const persistedGradingStatus = getSubmissionGradingStatus(submission);
            const workingGrading = isCurrentSubmission ? form : getSubmissionWorkingGrading(submission);
            const workingGradeOverrides = isCurrentSubmission
                ? gradeOverrides
                : getSubmissionWorkingGradeOverrides(submission);
            const hasWorkingReview =
                Object.keys(workingGrading.criteria).length > 0 ||
                (usesCompletionStatus && Boolean(workingGrading.status));
            const isPartiallyReviewed =
                persistedGradingStatus === 'in_progress' ||
                (persistedGradingStatus === 'not_started' && isCurrentSubmission && hasWorkingReview);
            const gradingStatus = isPartiallyReviewed ? 'in_progress' : persistedGradingStatus;
            const isGraded = gradingStatus === 'confirmed';
            const displayedGrading = isGraded ? getSubmissionWorkingGrading(submission) : workingGrading;
            const reviewSelections =
                isGraded || isPartiallyReviewed ? displayedGrading.criteria : getSuggestedGradeCriteria(submission);
            const completionStatus = usesCompletionStatus
                ? getEffectiveCompletionStatus({
                      criteria: rubricCriteria,
                      selections: displayedGrading.criteria,
                      status: displayedGrading.status,
                  })
                : null;
            const workingGrade =
                gradingStatus === 'in_progress' && !isUnscored
                    ? getGradingTotal(rubricCriteria, workingGrading, workingGradeOverrides, submission.ai?.grading)
                    : null;
            const confirmedGrade =
                isGraded && !isUnscored
                    ? getGradingTotal(rubricCriteria, submission.grading, submission.grade_overrides)
                    : null;

            return {
                key: id,
                value: getGradingNavigationTitle(submission, currentAssignment),
                gradingStatus,
                isGraded,
                isPartiallyReviewed,
                isUnscored,
                usesCompletionStatus,
                completionStatus,
                reviewWarning: shouldShowCompletionReviewWarning({
                    criteria: rubricCriteria,
                    isGraded,
                    selections: reviewSelections,
                    usesCompletionStatus,
                }),
                suggestedGrade: isUnscored ? null : (workingGrade ?? getSuggestedGrade(rubricCriteria, submission)),
                actualGrade: confirmedGrade,
                maxPossibleGrade: isUnscored
                    ? null
                    : cleanNumber(
                              rubricCriteria.reduce((acc, { options }) => {
                              const max = Math.max(...options.map(({ rate }) => Number(rate)));
                              return acc + max;
                          }, 0)
                          ),
                sections,
                section: sections.map(({ name }) => name).join(', '),
            };
        });
        return sortNavigationOptions(options, sort, isUnscored);
    }, [
        currentAssignment,
        currentSubmissionId,
        form,
        gradeOverrides,
        isUnscored,
        sort,
        submissions,
        usesCompletionStatus,
    ]);

    const headerNavigationOptions = useMemo(
        () => filterNavigationOptions(allHeaderNavigationOptions, sectionId),
        [allHeaderNavigationOptions, sectionId]
    );
    const filteredSubmissions = useMemo(() => {
        const ids = new Set(headerNavigationOptions.map(({ key }) => key));
        return submissions?.filter(({ id }) => ids.has(id));
    }, [headerNavigationOptions, submissions]);

    const currentSubmission = useMemo(() => {
        return submissions?.find(({ id }) => id === currentSubmissionId);
    }, [currentSubmissionId, submissions]);

    // Browser history can return to a submission outside the selected section.
    useEffect(() => {
        if (
            currentSubmission &&
            sectionId &&
            !headerNavigationOptions.some(({ key }) => key === currentSubmission.id)
        ) {
            setSelectedSectionId('');
        }
    }, [currentSubmission, headerNavigationOptions, sectionId]);

    // resolves the current submission's partial grading state.
    const gradingStatus = useMemo(() => getSubmissionGradingStatus(currentSubmission), [currentSubmission]);

    // these values are the persisted baseline for the working grading form.
    const persistedWorkingGrading = useMemo(() => getSubmissionWorkingGrading(currentSubmission), [currentSubmission]);
    const persistedWorkingGradeOverrides = useMemo(
        () => getSubmissionWorkingGradeOverrides(currentSubmission),
        [currentSubmission]
    );

    // these flags separate unsaved draft edits from confirmable grading work.
    const hasDraftChanges = useMemo(() => {
        if (!currentSubmission) return false;
        return (
            !isEqual(persistedWorkingGrading, form) ||
            !isEqual(persistedWorkingGradeOverrides, gradeOverrides) ||
            (currentSubmission.has_seen_feedback ?? false) !== meta.hasSeenFeedback
        );
    }, [
        currentSubmission,
        form,
        gradeOverrides,
        meta.hasSeenFeedback,
        persistedWorkingGradeOverrides,
        persistedWorkingGrading,
    ]);
    const hasConfirmableChanges = useMemo(() => {
        if (!currentSubmission) return false;
        if (currentSubmission.grading_draft) return true;
        if (!currentSubmission.grading) return true;

        return (
            !isEqual(getSubmissionWorkingGrading({ ...currentSubmission, grading_draft: undefined }), form) ||
            !isEqual(currentSubmission.grade_overrides ?? {}, gradeOverrides)
        );
    }, [currentSubmission, form, gradeOverrides]);

    const { feedbackRegenerationReasons, feedbackRegenerationNeededItemIds, feedbackUpdatedItemIds } = useMemo(
        () =>
            getFeedbackRegenerationState({
                assignment: currentAssignment,
                submission: currentSubmission,
                form,
                gradeOverrides,
            }),
        [currentAssignment, currentSubmission, form, gradeOverrides]
    );
    const pendingFeedbackItemIds = useMemo(
        () => getPendingFeedbackItemIds(currentSubmission?.feedback_regeneration?.active_batches),
        [currentSubmission?.feedback_regeneration?.active_batches]
    );
    // these payloads represent the latest and persisted draft snapshots.
    const draftSavePayload = useMemo(
        () =>
            currentSubmission
                ? getDraftSavePayload({ form, gradeOverrides, hasSeenFeedback: meta.hasSeenFeedback })
                : null,
        [currentSubmission, form, gradeOverrides, meta.hasSeenFeedback]
    );
    const persistedDraftSavePayload = useMemo(
        () =>
            currentSubmission
                ? getDraftSavePayload({
                      form: persistedWorkingGrading,
                      gradeOverrides: persistedWorkingGradeOverrides,
                      hasSeenFeedback: currentSubmission.has_seen_feedback ?? false,
                  })
                : null,
        [currentSubmission, persistedWorkingGradeOverrides, persistedWorkingGrading]
    );

    const isFormValid = useMemo(() => {
        if (!currentAssignment) return false;
        const isRubricEnabled =
            currentAssignment.features?.rubric !== false && currentAssignment.features?.assignment_rubric !== false;
        const isGradingValid =
            !isRubricEnabled || currentAssignment.rubric.criteria.every(({ id }) => Boolean(form.criteria[id]));
        const isFeedbackValid = Boolean(form.feedback);

        if (currentAssignment?.ai_feedback_enabled) {
            return isGradingValid && isFeedbackValid;
        } else if (currentAssignment?.ai_grading_enabled) {
            return isGradingValid;
        }
    }, [currentAssignment, form.criteria, form.feedback]);

    const areOverridesValid = useMemo(() => {
        if (isUnscored) return true;
        if (!Object.entries(gradeOverrides).length) return true;
        return Object.entries(gradeOverrides)
            .map(([criterionId, overrideValue]) => {
                if (criterionId === 'total') {
                    return (
                        isValidRate(overrideValue) &&
                        Number(overrideValue) >= 0 &&
                        Number(overrideValue) <= getRubricMaxPoints(currentAssignment.rubric.criteria)
                    );
                } else {
                    return (
                        isValidRate(overrideValue) &&
                        Number(overrideValue) >= 0 &&
                        Number(overrideValue) <=
                            getCriterionMaxPoints(
                                currentAssignment.rubric.criteria.find(({ id }) => id === criterionId)
                            )
                    );
                }
            })
            .reduce((acc, cur) => acc && cur, true);
    }, [currentAssignment?.rubric?.criteria, gradeOverrides, isUnscored]);

    const isCurrentFormValid = useMemo(() => isFormValid && areOverridesValid, [isFormValid, areOverridesValid]);

    const selectedCriterionTitle = currentAssignment?.rubric?.criteria?.find(
        ({ id }) => id === selectedCriterionId
    )?.title;

    // manages debounced partial grading persistence and explicit flushes.
    const {
        cancelScheduledSave,
        draftSaveError,
        enqueueSubmissionUpdate,
        flushDraftSave,
        isSavingDraft,
        pauseAutosave,
        resetDraftSave,
        resumeAutosave,
    } = useGradingDraftAutosave({
        enabled: Boolean(currentSubmission) && !joyrideRun,
        onError: handleDraftSaveError,
        onSaved: handleDraftSaved,
        payload: draftSavePayload,
        persistedPayload: persistedDraftSavePayload,
        submissionId: currentSubmissionId,
    });

    // provides unsaved partial grading context to instructor hugo.
    const unsavedGradeContext = useMemo(
        () =>
            buildUnsavedGradeContext({
                currentAssignment,
                currentSubmission,
                form,
                gradeOverrides,
                hasChanges: hasConfirmableChanges,
            }),
        [currentAssignment, currentSubmission, form, gradeOverrides, hasConfirmableChanges]
    );

    // merges a saved draft response into the submission list.
    function handleDraftSaved(savedSubmission, submissionId) {
        setSubmissions((current) =>
            current.map((submission) => {
                if (submission.id !== submissionId) return submission;
                return { ...savedSubmission, ...(submission.locked && { locked: true }) };
            })
        );
    }

    // reports background draft save failures.
    function handleDraftSaveError() {
        message.error(
            t('grading-section.draft-save-error', 'Your latest grading changes could not be saved. Please try again.')
        );
    }

    const getHighlights = useCallback(() => {
        const format = (items) => {
            return (items ?? []).map((item) => {
                return {
                    id: selectedInlineCommentId ?? selectedCriterionId ?? selectedFeedbackId,
                    value: item,
                };
            });
        };

        if (tab === 'grades' && selectedCriterionId) {
            const locations = currentSubmission?.ai?.grading?.[selectedCriterionId]?.locations;
            return format(locations);
        }

        if (tab === 'feedback' && selectedFeedbackId) {
            const feedback = currentSubmission?.ai?.feedback;
            const isRubric = feedback?.feedback_type === 'rubric';
            const suggestion = form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId);
            const locations = isRubric ? suggestion?.negative?.locations : suggestion?.locations;
            return format(locations);
        }

        const comments = form.inline_comments ?? [];
        const activeHighlights = comments
            .filter(({ active }) => active)
            .filter(({ id }) => id === selectedInlineCommentId)
            .map(({ highlight }) => highlight);

        return format(activeHighlights);
    }, [
        currentSubmission?.ai?.feedback,
        currentSubmission?.ai?.grading,
        form.feedback?.improvement_suggestions,
        form.inline_comments,
        selectedCriterionId,
        selectedFeedbackId,
        selectedInlineCommentId,
        tab,
    ]);

    // finds an adjacent submission in the current sorted order.
    const getAdjacentSubmissionId = (offset) => {
        if (!headerNavigationOptions?.length || !currentSubmission) return;

        const currentSubmissionIndex = headerNavigationOptions.findIndex(({ key }) => key === currentSubmission.id);
        if (currentSubmissionIndex === -1) return;

        const adjacentSubmissionIndex =
            (currentSubmissionIndex + offset + headerNavigationOptions.length) % headerNavigationOptions.length;
        return headerNavigationOptions[adjacentSubmissionIndex]?.key;
    };

    const getNextSubmissionId = () => getAdjacentSubmissionId(1);

    const getPreviousSubmissionId = () => getAdjacentSubmissionId(-1);

    // opens a retry dialog when a grading update fails.
    const openDraftSaveErrorDialog = useCallback(
        (retry) => {
            setDialog({
                open: true,
                message: t('dialog.grading-draft-save-failed.title', 'Unable to Save Grading Changes'),
                description: t(
                    'dialog.grading-draft-save-failed.description',
                    'Your latest grading changes must be saved before continuing. Please try again.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('grading-section.actions.retry-save', 'Try Again'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    return retry();
                },
                onCancel: closeDialog,
                onClose: closeDialog,
            });
        },
        [closeDialog, setDialog, t]
    );

    // runs a blocking persistence action with retry handling.
    const runWithPersistenceRetry = useCallback(
        (action) => {
            const attempt = async () => {
                pauseAutosave();
                try {
                    await action();
                } catch {
                    openDraftSaveErrorDialog(attempt);
                } finally {
                    resumeAutosave();
                }
            };

            return attempt();
        },
        [openDraftSaveErrorDialog, pauseAutosave, resumeAutosave]
    );

    // flushes the latest draft before an action with separate error handling.
    const flushDraftBeforeAction = useCallback(
        (action) => {
            const attempt = async () => {
                pauseAutosave();
                let didFlush = false;
                try {
                    await flushDraftSave();
                    didFlush = true;
                } catch {
                    openDraftSaveErrorDialog(attempt);
                } finally {
                    resumeAutosave();
                }
                if (didFlush) await action();
            };

            return attempt();
        },
        [flushDraftSave, openDraftSaveErrorDialog, pauseAutosave, resumeAutosave]
    );

    // combines the current draft and elapsed-time session into one update.
    const getCurrentSubmissionUpdate = (reason) => {
        const session = createSession(reason);
        const submissionWithSession = addSessionToSubmission(session, currentSubmission);
        const nextDraftPayload = hasDraftChanges || isSavingDraft || draftSaveError ? draftSavePayload : undefined;

        return {
            draftPayload: nextDraftPayload,
            payload: {
                ...(nextDraftPayload ?? {}),
                sessions: submissionWithSession.sessions,
            },
        };
    };

    // navigates immediately while the source submission update remains queued.
    const navigateFromGrading = (path, reason) => {
        const sourceSubmissionId = currentSubmissionId;
        const update = getCurrentSubmissionUpdate(reason);
        cancelScheduledSave();
        setSubmissions((current) =>
            current.map((submission) =>
                submission.id === sourceSubmissionId
                    ? { ...submission, ...update.payload, modified_on: new Date().getTime() }
                    : submission
            )
        );
        enqueueSubmissionUpdate({
            submissionId: sourceSubmissionId,
            payload: update.payload,
            draftPayload: update.draftPayload,
        }).catch(() => undefined);
        history.push(path);
    };

    // fills untouched draft criteria with the current ai suggestions.
    const handleClickAcceptSuggestions = () => {
        setForm((current) => {
            return {
                ...current,
                criteria: {
                    ...current.criteria,
                    ...getSuggestedGradeCriteria(currentSubmission, [
                        ...Object.keys(current.criteria),
                        ...Object.keys(gradeOverrides),
                    ]),
                },
            };
        });
    };

    // confirms the working draft and resets the autosave baseline.
    const handleClickConfirm = (shouldGoNext) => {
        if (joyrideRun) return performNext();
        document.getElementById('actions-container')?.scroll({ top: 0, behavior: 'smooth' });
        const nextSubmissionId = shouldGoNext ? getNextSubmissionId() : null;

        return runWithPersistenceRetry(async () => {
            cancelScheduledSave();
            let updatedForm =
                currentSubmission.ai?.feedback?.feedback_type === 'templated'
                    ? {
                          ...form,
                          feedback: {
                              ...form.feedback,
                              commentary: getTemplatedFeedback(currentAssignment, form),
                          },
                      }
                    : form;
            if (usesCompletionStatus) {
                updatedForm = { ...updatedForm, status: normalizeCompletionStatus(updatedForm.status, 'complete') };
            }
            const session = createSession('confirm');
            const submissionWithSession = addSessionToSubmission(session, currentSubmission);
            const normalizedGradeOverrides = isUnscored
                ? {}
                : Object.fromEntries(
                      Object.entries(gradeOverrides).map(([key, value]) => [key, String(Number(value))])
                  );
            const savedSubmission = await enqueueSubmissionUpdate({
                submissionId: currentSubmissionId,
                applyResponse: false,
                notifyOnError: false,
                payload: {
                    grading: omit(updatedForm, ['grading_explanations']),
                    grading_explanations: updatedForm.grading_explanations ?? {},
                    grade_overrides: normalizedGradeOverrides,
                    grading_status: 'confirmed',
                    has_seen_feedback: true,
                    sessions: submissionWithSession.sessions,
                },
            });
            const confirmedForm = getSubmissionWorkingGrading(savedSubmission);
            const confirmedGradeOverrides = getSubmissionWorkingGradeOverrides(savedSubmission);

            resetDraftSave(
                getDraftSavePayload({
                    form: confirmedForm,
                    gradeOverrides: confirmedGradeOverrides,
                    hasSeenFeedback: true,
                })
            );
            initializedSubmissionIdRef.current = savedSubmission.id;
            setForm(confirmedForm);
            setGradeOverrides(confirmedGradeOverrides);
            setMeta({
                hasSeenFeedback: true,
                sessionStart: new Date().getTime(),
                excludedTime: 0,
            });
            handleDraftSaved({ ...savedSubmission, locked: true }, currentSubmissionId);

            if (nextSubmissionId) {
                history.push(`/assignments/${currentAssignmentId}/grading/${nextSubmissionId}`);
            } else {
                setSubmissions((current) => current.map((submission) => omit(submission, ['locked'])));
                history.push(`/assignments/${currentAssignmentId}`);
            }
        });
    };

    const handleClickNext = () => {
        const nextSubmissionId = getNextSubmissionId();
        if (!nextSubmissionId) return;
        navigateFromGrading(`/assignments/${currentAssignmentId}/grading/${nextSubmissionId}`, 'skip');
    };

    const handleClickPrevious = () => {
        const previousSubmissionId = getPreviousSubmissionId();
        if (!previousSubmissionId) return;
        navigateFromGrading(`/assignments/${currentAssignmentId}/grading/${previousSubmissionId}`, 'skip');
    };

    const handleSelectSubmission = (submissionId) => {
        navigateFromGrading(`/assignments/${currentAssignmentId}/grading/${submissionId}`, 'skip');
    };

    const handleSelectSection = (nextSectionId) => {
        const options = filterNavigationOptions(allHeaderNavigationOptions, nextSectionId);
        if (!options.length) return;
        setSelectedSectionId(nextSectionId);
        if (!options.some(({ key }) => key === currentSubmissionId)) {
            handleSelectSubmission(options[0].key);
        }
    };

    const handleClickClose = () => {
        return runWithPersistenceRetry(async () => {
            cancelScheduledSave();
            const sourceSubmissionId = currentSubmissionId;
            const update = getCurrentSubmissionUpdate('exit');
            const savedSubmission = await enqueueSubmissionUpdate({
                submissionId: sourceSubmissionId,
                payload: update.payload,
                draftPayload: update.draftPayload,
                applyResponse: false,
                notifyOnError: false,
            });
            handleDraftSaved(savedSubmission, sourceSubmissionId);
            setSubmissions((current) => current.map((submission) => omit(submission, ['locked'])));
            history.push(`/assignments/${currentAssignmentId}`);
        });
    };

    // opens the regeneration dialog without starting a batch
    const handleClickRegenerateFeedback = () => {
        setIsFeedbackRegenerationDialogOpen(true);
    };

    // flushes grading changes and creates one batch for the selected feedback
    const handleCreateFeedbackRegenerationBatch = async ({ targetItemIds, customPrompt }) => {
        const availableItemIds = targetItemIds.filter((itemId) => !pendingFeedbackItemIds.includes(itemId));
        if (!availableItemIds.length) {
            message.error(
                t('grading-section.feedback-regeneration-unavailable', 'The selected feedback is already regenerating.')
            );
            return false;
        }

        let didStart = false;
        await flushDraftBeforeAction(async () => {
            try {
                const createdBatch = await batchService.createSubmissions({
                    id: uuid(),
                    type: 'feedback-regeneration',
                    submission_id: currentSubmission.id,
                    target_item_ids: availableItemIds,
                    context: {
                        criteria: { ...(form.criteria ?? {}) },
                        grade_overrides: { ...gradeOverrides },
                        grading_explanations: { ...(form.grading_explanations ?? {}) },
                        custom_prompt: customPrompt,
                    },
                });
                const reservedItemIds = createdBatch.target_item_ids ?? availableItemIds;
                setSubmissions((current) =>
                    current.map((submission) => {
                        if (submission.id !== currentSubmission.id) return submission;
                        return {
                            ...submission,
                            feedback_regeneration: {
                                ...submission.feedback_regeneration,
                                active_batches: [
                                    ...(submission.feedback_regeneration?.active_batches ?? []),
                                    { batch_id: createdBatch.id, item_ids: reservedItemIds },
                                ],
                            },
                        };
                    })
                );
                setBatch((current) => [...(current ?? []), createdBatch]);
                didStart = true;
                setIsFeedbackRegenerationDialogOpen(false);
            } catch (error) {
                if (error.response?.status === 409) {
                    message.error(
                        t(
                            'grading-section.feedback-regeneration-conflict',
                            'One or more selected feedback items are already regenerating.'
                        )
                    );
                } else {
                    message.error(
                        t('grading-section.feedback-regeneration-failed', 'Feedback regeneration could not be started.')
                    );
                }
            }
        });

        return didStart;
    };

    const handleRevertFeedback = async ({ historyId, itemIds }) => {
        if (!historyId || !itemIds?.length) return false;

        let didRevert = false;
        await flushDraftBeforeAction(async () => {
            try {
                const updatedSubmission = await submissionService.updateSubmission(currentSubmission.id, {
                    operation: 'revert-feedback',
                    history_id: historyId,
                    item_ids: itemIds,
                });
                const nextForm = getSubmissionWorkingGrading(updatedSubmission);
                const nextGradeOverrides = getSubmissionWorkingGradeOverrides(updatedSubmission);
                const nextHasSeenFeedback = updatedSubmission.has_seen_feedback ?? meta.hasSeenFeedback;
                handleDraftSaved(updatedSubmission, currentSubmission.id);
                setForm(nextForm);
                setGradeOverrides(nextGradeOverrides);
                resetDraftSave(
                    getDraftSavePayload({
                        form: nextForm,
                        gradeOverrides: nextGradeOverrides,
                        hasSeenFeedback: nextHasSeenFeedback,
                    })
                );
                didRevert = true;
                message.success(t('grading-section.feedback-reverted', 'Feedback reverted.'));
            } catch {
                message.error(
                    t('grading-section.feedback-revert-failed', 'Feedback could not be reverted. Please try again.')
                );
            }
        });

        return didRevert;
    };

    const handleSelectDropdown = async ({ key }) => {
        if (key === 'regenerate-grades') {
            setDialog({
                open: true,
                message: t('dialog.regenerate-suggested-grades.title', 'Regenerate Suggested Grades?'),
                description: t(
                    'dialog.regenerate-suggested-grades.description',
                    'This will delete any existing grades permanently.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.regenerate', 'Regenerate'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: async () => {
                    closeDialog();
                    setIsGradesGenerating(true);
                    const account = await getAccount();
                    await getAIPreview({
                        target: 'grading',
                        course_id: course.id,
                        owner_account_id: currentAssignment.owner_account_id,
                        assignment: currentAssignment,
                        submission: currentSubmission,
                        connections: account.connections.map(({ id }) => id),
                        language: course.language ?? 'English',
                    });
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
        if (key === 'copy-all') {
            const noFeedbackTranslationArgs = currentAssignment?.api_assignment_id
                ? [
                      'feedback-section.no-feedback-no-pushback',
                      'No feedback provided. This message will not be passed back.',
                  ]
                : ['feedback-section.no-feedback', 'No feedback provided.'];

            let feedback = '';
            if (typeof currentSubmission.ai?.feedback === 'string') {
                feedback += stripHTML(form.feedback);
            } else {
                feedback += stripHTML(form.feedback.commentary);
                if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                    form.feedback.improvement_suggestions.forEach(({ criterion_id, positive, negative }, index) => {
                        if (index || form.feedback.commentary) {
                            feedback += '\n\n';
                        }
                        const criterion = currentAssignment.rubric.criteria.find(({ id }) => id === criterion_id);
                        feedback += `${criterion.title}\n\n`;
                        feedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
                        feedback +=
                            !positive.active && positive.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : positive.text === ''
                                  ? t(...noFeedbackTranslationArgs)
                                  : stripHTML(positive.text);
                        feedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
                        feedback +=
                            !negative.active && negative.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : negative.text === ''
                                  ? t(...noFeedbackTranslationArgs)
                                  : stripHTML(negative.text);
                    });
                } else if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                    form.feedback.improvement_suggestions
                        .filter(({ active }) => active)
                        .forEach(({ text }, index) => {
                            if (index || form.feedback.commentary) {
                                feedback += '\n\n';
                            }
                            feedback += stripHTML(text);
                        });
                }
            }
            await navigator.clipboard.writeText(feedback);
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: t('dialog.delete-all-feedback.title', 'Delete All Feedback?'),
                description: t(
                    'dialog.delete-all-feedback.description',
                    'Are you sure you want to delete all feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        positive: {
                                            ...item.positive,
                                            active: false,
                                        },
                                        negative: {
                                            ...item.negative,
                                            active: false,
                                        },
                                    })),
                                },
                            };
                        });
                    }
                    if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        active: false,
                                    })),
                                },
                            };
                        });
                    }
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleCriterionNavigation = (criterionId) => {
        setTab((current) => {
            if (current === 'grades') {
                const matchedSuggestion = form?.feedback?.improvement_suggestions?.find(
                    (s) => s.criterion_id === criterionId
                );
                if (matchedSuggestion) {
                    setSelectedFeedbackId(matchedSuggestion.id);
                    setTimeout(() => {
                        scrollElementIntoView(matchedSuggestion.id);
                    }, 100);
                }
                return 'feedback';
            }
            if (current === 'feedback') {
                setSelectedCriterionId(criterionId);
                setTimeout(() => {
                    scrollElementIntoView(criterionId);
                }, 100);
                return 'grades';
            }
            return current;
        });
    };

    // publishes unsaved partial grading to instructor hugo.
    useEffect(() => {
        setInstructorHugoLiveContext?.(unsavedGradeContext);

        return () => setInstructorHugoLiveContext?.(null);
    }, [setInstructorHugoLiveContext, unsavedGradeContext]);

    // flushes partial grading before guarded app navigation.
    useEffect(() => {
        onBeforeNavigate((navigate) => {
            flushDraftBeforeAction(navigate);
        });

        return () => onBeforeNavigate(null);
    }, [flushDraftBeforeAction, onBeforeNavigate]);

    useEffect(() => {
        if (tab === 'feedback') {
            setMeta((current) => ({ ...current, hasSeenFeedback: true }));
        }
    }, [tab]);

    useEffect(() => {
        if (submissions && !submissions.length) {
            history.push(`/assignments/${currentAssignmentId}`);
        }
    }, [currentAssignmentId, history, submissions]);

    useEffect(() => {
        if (headerNavigationOptions.length && !currentSubmissionId) {
            const nextSubmissionId = headerNavigationOptions[0].key;
            history.replace(`/assignments/${currentAssignmentId}/grading/${nextSubmissionId}`);
        }
    }, [currentAssignmentId, currentSubmissionId, headerNavigationOptions, history]);

    useEffect(() => {
        if (!currentSubmission?.id) return;
        setTab('grades');
    }, [currentSubmission?.id]);

    // loads persisted partial grading when the selected submission changes.
    useEffect(() => {
        if (!currentAssignment) return;
        if (!currentSubmission) {
            initializedSubmissionIdRef.current = null;
            syncedFeedbackHistoryRef.current = { historyId: null, submissionId: null };
            setSelectedFileId('');
            setSelectedCriterionId(null);
            setSelectedFeedbackId(null);
            setSelectedInlineCommentId(null);
            return;
        }
        if (initializedSubmissionIdRef.current === currentSubmission.id) return;
        initializedSubmissionIdRef.current = currentSubmission.id;
        syncedFeedbackHistoryRef.current = {
            historyId: getLatestFeedbackHistoryId(currentSubmission),
            submissionId: currentSubmission.id,
        };

        setSelectedFileId(currentSubmission.files[0].id);
        setSelectedCriterionId(currentAssignment.rubric.criteria[0].id);
        document.getElementById('actions-container').scrollTop = 0;
        const workingGrading = getSubmissionWorkingGrading(currentSubmission);
        setForm(workingGrading);
        setGradeOverrides(isUnscored ? {} : getSubmissionWorkingGradeOverrides(currentSubmission));
        setMeta({
            hasSeenFeedback: currentSubmission.has_seen_feedback ?? !currentSubmission.ai,
            sessionStart: new Date().getTime(),
            excludedTime: 0,
        });

        if (['rubric', 'system'].includes(currentSubmission.ai?.feedback?.feedback_type)) {
            const activeSuggestions = workingGrading.feedback?.improvement_suggestions?.filter(({ active }) => active);
            if (activeSuggestions?.length) setSelectedFeedbackId(activeSuggestions[0].id);
        }
    }, [currentAssignment, currentSubmission, isUnscored]);

    // merges completed regeneration feedback without replacing newer local grading edits.
    useEffect(() => {
        if (!currentSubmission || initializedSubmissionIdRef.current !== currentSubmission.id) return;
        const historyId = getLatestFeedbackHistoryId(currentSubmission);
        const feedbackHistory = currentSubmission.feedback_history ?? [];
        const syncedHistory = syncedFeedbackHistoryRef.current;
        if (
            !historyId ||
            (syncedHistory.submissionId === currentSubmission.id && syncedHistory.historyId === historyId)
        ) {
            return;
        }

        // History is append-only; a late response must not move the synchronization point backward.
        if (syncedHistory.historyId && !feedbackHistory.some(({ id }) => id === syncedHistory.historyId)) return;

        const persistedForm = getSubmissionWorkingGrading(currentSubmission);
        const persistedGradeOverrides = getSubmissionWorkingGradeOverrides(currentSubmission);
        const persistedHasSeenFeedback = currentSubmission.has_seen_feedback ?? meta.hasSeenFeedback;
        // An in-flight draft save can still contain older text for a regenerated item.
        const regeneratedFeedback = currentSubmission.ai?.feedback ?? persistedForm.feedback;
        syncedFeedbackHistoryRef.current = { historyId, submissionId: currentSubmission.id };
        resetDraftSave(
            getDraftSavePayload({
                form: persistedForm,
                gradeOverrides: persistedGradeOverrides,
                hasSeenFeedback: persistedHasSeenFeedback,
            })
        );
        setForm((current) => ({
            ...current,
            feedback: mergeFeedbackHistoryUpdates(
                current.feedback,
                regeneratedFeedback,
                feedbackHistory,
                syncedHistory.historyId
            ),
        }));
    }, [currentSubmission, meta.hasSeenFeedback, resetDraftSave]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    useEffect(() => {
        setNextCallback({
            8: () => setTab('feedback'),
            9: () => {
                setForm((current) => ({
                    ...current,
                    criteria: {
                        c1: 'c1-o1',
                        c2: 'c2-o2',
                        c3: 'c3-o2',
                        c4: 'c4-o3',
                    },
                }));
            },
        });
        setPrevCallback({
            8: () => setTab('grades'),
            10: () => {
                setForm((current) => ({ ...current, criteria: {} }));
            },
        });
    }, [setNextCallback, setPrevCallback]);

    useEffect(() => {
        const cleanup = onMessage(async ({ data }) => {
            if (data?.payload_type === 'regenerate_submission') {
                if (!isGradesGenerating) return;
                const { submission } = data.payload;
                const updatedSubmission = {
                    ...currentSubmission,
                    modified_on: new Date().getTime(),
                };
                updatedSubmission.ai.grading = submission.data.grading;
                setSubmissions((current) => {
                    return current.map((submission) => {
                        if (submission.id !== updatedSubmission.id) return submission;
                        setTimeout(() => {
                            setForm(form);
                            setGradeOverrides(gradeOverrides);
                            setMeta(meta);
                        }, 50);
                        return updatedSubmission;
                    });
                });
                try {
                    await submissionService.updateSubmission(updatedSubmission.id, {
                        ai: updatedSubmission.ai,
                        has_seen_feedback: updatedSubmission.has_seen_feedback,
                        ...(updatedSubmission.grading && { grading: updatedSubmission.grading }),
                    });
                } catch {
                    message.error(t('common:message.something-went-wrong', 'Something went wrong.'));
                }
                setIsGradesGenerating(false);
            }
        });
        return cleanup;
    }, [currentSubmission, form, gradeOverrides, isGradesGenerating, meta, message, onMessage, setSubmissions, tab, t]);

    useEffect(() => {
        if (selectedCriterionId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedCriterionId]);

    useEffect(() => {
        if (selectedFeedbackId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedFeedbackId]);

    useEffect(() => {
        if (selectedInlineCommentId !== null) {
            if (tab === 'grades') {
                setSelectedCriterionId(null);
            }
            if (tab === 'feedback') {
                setSelectedFeedbackId(null);
            }
        }
    }, [selectedInlineCommentId, tab]);

    if (submissions?.length && currentSubmissionId && !currentSubmission) {
        return <Redirect to={`/assignments/${currentAssignmentId}`} />;
    }

    return (
        <>
            <PageContext.Provider
                value={{
                    currentAssignment,
                    submissions,
                    filteredSubmissions,
                    headerNavigationOptions,
                    sectionOptions,
                    sectionId,
                    isGroupAssignment,
                    currentSubmissionId,
                    currentSubmission,
                    gradingStatus,
                    hasDraftChanges,
                    hasConfirmableChanges,
                    draftSaveError,
                    isSavingDraft,
                    selectedCriterionTitle,
                    highlights: getHighlights(),
                    selectedFileId,
                    setSelectedFileId,
                    sort,
                    setSort,
                    form,
                    setForm,
                    isCurrentFormValid,
                    gradeOverrides,
                    setGradeOverrides,
                    meta,
                    tab,
                    setTab,
                    selectedCriterionId,
                    setSelectedCriterionId,
                    selectedFeedbackId,
                    setSelectedFeedbackId,
                    selectedInlineCommentId,
                    setSelectedInlineCommentId,
                    isGradesGenerating,
                    feedbackRegenerationReasons,
                    feedbackRegenerationNeededItemIds,
                    feedbackUpdatedItemIds,
                    pendingFeedbackItemIds,
                    showFailedDialog,
                    setShowFailedDialog,
                    isDocumentFullScreen,
                    setIsDocumentFullScreen,
                    handleClickAcceptSuggestions,
                    handleClickConfirm,
                    handleClickNext,
                    handleClickPrevious,
                    handleSelectSubmission,
                    handleSelectSection,
                    handleClickClose,
                    handleClickRegenerateFeedback,
                    handleRevertFeedback,
                    handleSelectDropdown,
                    handleCriterionNavigation,
                }}
                {...props}
            />
            <FeedbackRegenerationDialog
                assignment={currentAssignment}
                feedback={form.feedback}
                open={isFeedbackRegenerationDialogOpen}
                pendingItemIds={pendingFeedbackItemIds}
                suggestedItemIds={feedbackRegenerationNeededItemIds}
                onClose={() => setIsFeedbackRegenerationDialogOpen(false)}
                onRegenerate={handleCreateFeedbackRegenerationBatch}
            />
        </>
    );
};

export { PageContext };
export default PageProvider;
